import React, { useContext, useMemo, useReducer, useState } from "react";
import { LLM_OPTIONS, OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { ALL_METRIC_VALUES } from "./metricOptions";
import { AuthContext } from "../../context/auth-context";
import { resolveDefaultKeywordOptions } from "../../utils/keywordOptions";

const sanitizeKey = (key) => key.replace(/[.#$\/\[\]]/g, "_");

const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  return Object.keys(obj).reduce((result, key) => {
    result[sanitizeKey(key)] = sanitizeObject(obj[key]);
    return result;
  }, {});
};

const updateScores = (scores, llmList, categories, markets = [], personas = []) => {
  if (typeof markets === "string") markets = [markets];
  if (!Array.isArray(markets)) markets = [];
  if (typeof llmList === "string") llmList = [llmList];
  if (!Array.isArray(llmList)) llmList = [];
  if (typeof categories === "string") categories = [categories];
  if (!Array.isArray(categories)) categories = [];

  const personaTitles = personas
    .map((p) => (typeof p === "object" && p?.title ? p.title : typeof p === "string" ? p : null))
    .filter(Boolean);

  llmList.forEach((llm) => {
    if (!scores[llm]) scores[llm] = {};
    categories.forEach((cat) => {
      if (!markets.length) {
        if (!scores[llm][cat]) scores[llm][cat] = [];
        return;
      }
      if (!scores[llm][cat]) scores[llm][cat] = {};
      markets.forEach((market) => {
        if (!personaTitles.length) {
          if (!scores[llm][cat][market]) scores[llm][cat][market] = [];
          return;
        }
        if (!scores[llm][cat][market]) scores[llm][cat][market] = {};
        personaTitles.forEach((persona) => {
          if (!scores[llm][cat][market][persona]) scores[llm][cat][market][persona] = [];
        });
      });
    });
  });

  return { ...scores };
};

const resolveSelectionRules = (state, nextPersona, nextMarket, nextCategory) => {
  const personaSettings = state.state?.personaSettings || {};
  const applyAllMarkets = personaSettings.applyPersonaToAllMarkets;
  const applyAllCategory = personaSettings.applyPersonaToAllCategory;

  const primaryPersona = state.persona?.[0]?.title ?? state.persona?.[0];
  const primaryMarket = state.markets?.[0];
  const primaryCategory = state.category?.[0];

  const requestedPersona = nextPersona ?? state.state?.selectedPersona;

  let persona = requestedPersona;
  let market = nextMarket ?? state.state?.selectedMarket;
  let category = nextCategory ?? state.state?.selectedCategory;

  let isCombinationAllowed = true;

  // CASE 4: persona applies to all categories and all markets — no restriction
  if (applyAllCategory && applyAllMarkets) {
    return { persona, market, category, isCombinationAllowed };
  }

  // CASE 1: persona only applies to primary category + primary market
  if (!applyAllCategory && !applyAllMarkets) {
    if (category !== primaryCategory || market !== primaryMarket) {
      persona = primaryPersona;
    }
  }

  // CASE 2: persona applies to all markets but only primary category
  if (!applyAllCategory && applyAllMarkets) {
    if (category !== primaryCategory) {
      persona = primaryPersona;
    }
  }

  // CASE 3: persona applies to all categories but only primary market
  if (applyAllCategory && !applyAllMarkets) {
    if (market !== primaryMarket) {
      persona = primaryPersona;
    }
  }

  if (persona !== requestedPersona) {
    isCombinationAllowed = false;
  }

  return { persona, market, category, isCombinationAllowed };
};

const CA_INITIAL_NESTED_STATE = {
  id: null,
  transactionId: null,
  finalTransactionId: null,
  customTemplateID: null,
  transactionStatus: "",
  finalTransactionStatus: "INITIATED",
  sourceValidationStatus: "PENDING",
  selectedLLM: "gpt-5.6",
  selectedCompetitor: 0,
  selectedCategory: "",
  selectedMarket: "",
  selectedPersona: "General Persona",
  status: {},
  rbiType: "propensityToRecommend",
  keywords: ["Top", "Best", "Most Recommended"],
  isBatchMode: false,
  rankCompetitiveSet: [],
  rankMethodology: "by_category",
  selectedMetrics: ALL_METRIC_VALUES,
  showReportCheckbox: false,
  selectAll: false,
  selectedSection: {},
  personaSettings: { applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false },
  conversationSettings: { selectedSites: [], excludeBrandOwnedSources: false },
  conversationEnabled: false,
  metricSettingObject: {},
  showAnimation: true,
  launchRecord: [],
  transactionHistory: [],
  compareMode: "default",
  multiLLMs: [],
  isShared: false,
  sharedAt: null,
  ownerUserId: null,
};

const CA_INITIAL_STATE = {
  name: "",
  entity: "",
  category: [],
  market: "",
  markets: [],
  persona: [],
  llm: ["gpt-5.6"],
  primary_llm: "gpt-5.6",
  type: "brand",
  domain: "",
  transactionStatus: "",
  sourceValidationStatus: "PENDING",
  finalTransactionStatus: "INITIATED",
  loading: false,
  competitors: {},
  brand_scores: {},
  category_dimensions: {},
  core_attributes: {},
  sources: {},
  brand_description: {},
  positive_negative_attributes: {},
  is_launched: false,
  abort_launch: false,
  formData: false,
  is_resetFormState: false,
  location: "United States",
  occupation: "",
  attributes: [],
  pre_category_dimensions: [],
  selectedCompetitor: 0,
  rank_scores: {},
  composite_rank_score: {},
  showAnimation: true,
  selectAll: false,
  selectedSection: {},
  status: {},
  transactionHistory: [],
  launchRecord: [],
  rbiType: "propensityToRecommend",
  keywords: ["Top", "Best", "Most Recommended"],
  isBatchMode: false,
  selectedMetrics: ALL_METRIC_VALUES,
  auditLimits: null,
  isCombinationAllowed: true,
  metricSettingObject: {},
};

export const CategoryAnalysisContext = React.createContext({
  state: {},
  status: {},
  category: [],
  market: "",
  markets: [],
  llm: [],
  persona: [],
  type: "brand",
  loading: false,
  formData: false,
  is_resetFormState: false,
  conversationEnabled: false,
  entity: "",
  name: "",
  finalTransactionId: "",
  primary_llm: "",
  customTemplateID: null,
  brand_scores: {},
  sources: {},
  core_attributes: {},
  competitors: {},
  category_dimensions: {},
  brand_description: {},
  positive_negative_attributes: {},
  rank_scores: {},
  is_launched: false,
  abort_launch: false,
  isCombinationAllowed: true,
  personaSettings: { applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false },
  conversationSettings: { selectedSites: [], excludeBrandOwnedSources: false },
  // update functions
  updateId: () => {},
  updateLoading: () => {},
  updateEntity: () => {},
  updateName: () => {},
  updateType: () => {},
  updateCategory: () => {},
  updateMarket: () => {},
  updateLLM: () => {},
  updatePersona: () => {},
  updateBrandScores: () => {},
  replaceBrandScores: () => {},
  updateSources: () => {},
  updateCoreAttributes: () => {},
  updatedCompetitors: () => {},
  updateCategoryDimensions: () => {},
  updateBrandDescription: () => {},
  updatePositiveNegativeAttributes: () => {},
  updateRankScores: () => {},
  updateCompositeRankScores: () => {},
  composite_rank_score: {},
  updateTransactionId: () => {},
  updateFinalTransactionId: () => {},
  updateCustomTemplateID: () => {},
  updateTransactionStatus: () => {},
  updateFinalTransactionStatus: () => {},
  updateTransactionHistory: () => {},
  updateSelectedLLM: () => {},
  updateSelectedCompetitor: () => {},
  updateSelectedCategory: () => {},
  updateSelectedMarket: () => {},
  updateSelectedPersona: () => {},
  updateStatus: () => {},
  updateLaunchRecord: () => {},
  updateFormData: () => {},
  updateLaunchTransaction: () => {},
  updateSourceValidationStatus: () => {},
  updatePersonaSettings: () => {},
  updateRbiType: () => {},
  updateKeywords: () => {},
  updateIsBatchMode: () => {},
  updateRankCompetitiveSet: () => {},
  updateRankMethodology: () => {},
  updateConversationSetting: () => {},
  updatePrimaryLLM: () => {},
  updateLaunch: () => {},
  updatedLaunched: () => {},
  updateAbortLaunch: () => {},
  resetState: () => {},
  refreshState: () => {},
  resetForRelaunch: () => {},
  resetFormState: () => {},
  cancelSelection: () => {},
  showAnimation: () => {},
  showReportCheckbox: () => {},
  clickComponentCheckbox: () => {},
  setMetricSettingObj: () => {},
  mapMetricTitle: () => null,
  updateConversationEnabled: () => {},
  updateShareInfo: () => {},
});

function caReducer(state = CA_INITIAL_STATE, action) {
  switch (action.type) {
    case "UPDATE_BRAND_SCORE": {
      const { req, res, sentimentSources, isValidated } = action.payload;
      const { data, index } = res;
      const { llm, category, market, persona } = req;

      const existingScores = { ...state.brand_scores };
      if (!existingScores[llm]?.[category]?.[market]?.[persona]) return state;

      const updatedList = [...existingScores[llm][category][market][persona]];
      const rankScores = state.rank_scores[llm]?.[category]?.[market]?.[persona];
      const rankDataForBrand = rankScores?.data?.find(
        (d) => d.name?.toLowerCase() === data?.name?.toLowerCase()
      );

      if (rankDataForBrand && data?.name) {
        updatedList[index] = {
          name: data.name,
          rank: rankDataForBrand.score,
          favorability: data.favorability,
          total: +rankDataForBrand.score + +data.favorability,
          extras: {
            ...data.extras,
            ...rankScores.extras,
            rank: [rankDataForBrand],
            sentimentSources,
            isValidated,
            rankSources: rankScores.sources,
          },
          rbiType: data?.rbiType,
        };
      } else {
        updatedList[index] = data;
      }

      existingScores[llm][category][market][persona] = updatedList;
      return { ...state, brand_scores: existingScores };
    }

    case "REPLACE_BRAND_SCORES": {
      return { ...state, brand_scores: action.payload };
    }

    case "UPDATE_SOURCES": {
      const updatedSources = { ...state.sources };
      const { res, req, showOldSourcesRecord, type } = action.payload;
      const { llm, category, market, persona } = req;

      if (!updatedSources[llm]) updatedSources[llm] = {};
      if (!updatedSources[llm][category]) updatedSources[llm][category] = {};
      if (!updatedSources[llm][category][market] || Array.isArray(updatedSources[llm][category][market])) {
        updatedSources[llm][category][market] = {};
      }
      if (!updatedSources[llm][category][market][persona]) {
        updatedSources[llm][category][market][persona] = {};
      }

      if (!showOldSourcesRecord) {
        const newSourcesArray = Array.isArray(res) ? res : [res];
        const prevSourcesArray = updatedSources?.[llm]?.[category]?.[market]?.[persona]?.[type] || [];
        updatedSources[llm][category][market][persona][type] = [...prevSourcesArray, ...newSourcesArray];
      } else {
        const mergedSources = Array.isArray(res) ? res.reduce((acc, obj) => ({ ...acc, ...obj }), {}) : res;
        const prevSources = updatedSources?.[llm]?.[category]?.[market]?.[persona]?.[type] || {};
        updatedSources[llm][category][market][persona][type] = { ...prevSources, ...mergedSources };
      }

      return { ...state, sources: updatedSources };
    }

    case "UPDATE_CORE_ATTRIBUTES": {
      const { category_averages, products, summary, baseResponses, llm, category, market, persona, isValidated } = action.payload;
      const updated = { ...state.core_attributes };
      if (!updated[llm]) updated[llm] = {};
      if (!updated[llm][category]) updated[llm][category] = {};
      if (!updated[llm][category][market]) updated[llm][category][market] = {};
      updated[llm][category][market][persona] = { category_averages, products, summary, baseResponses, isValidated };
      return { ...state, core_attributes: updated };
    }

    case "UPDATE_RANK_SCORES": {
      const { req, res } = action.payload;
      const { data, extras, sources } = res;
      const { llm, category, market, persona } = req;

      const existingBrandScores = { ...state.brand_scores };
      const currentList = existingBrandScores[llm]?.[category]?.[market]?.[persona];
      let updatedBrandScores = Array.isArray(currentList) ? [...currentList] : [];

      Array.isArray(data) && data.forEach((item, index) => {
        // Competitors are stored 0-based — no primary brand slot in CA
        const storeIdx = index;
        const brandIndexItem = updatedBrandScores[storeIdx];
        if (brandIndexItem && brandIndexItem.favorability) {
          updatedBrandScores[storeIdx] = {
            name: item.name,
            rank: item.score,
            favorability: brandIndexItem.favorability,
            total: +item.score + +brandIndexItem.favorability,
            extras: {
              ...extras,
              ...brandIndexItem.extras,
              rank: [item],
              sentimentSources: brandIndexItem.sentimentSources,
              isValidated: brandIndexItem.isValidated,
              rankSources: sources,
            },
            rbiType: brandIndexItem?.rbiType,
          };
        } else {
          updatedBrandScores[storeIdx] = {
            ...updatedBrandScores[storeIdx],
            name: item.name,
            rank: item.score,
            extras: {
              ...extras,
              rank: [item],
            },
          };
        }
      });

      if (existingBrandScores[llm]?.[category]?.[market]?.[persona] !== undefined) {
        existingBrandScores[llm][category][market][persona] = updatedBrandScores;
      }

      const updatedRankScores = { ...state.rank_scores };
      if (!updatedRankScores[llm]) updatedRankScores[llm] = {};
      if (!updatedRankScores[llm][category]) updatedRankScores[llm][category] = {};
      if (!updatedRankScores[llm][category][market]) updatedRankScores[llm][category][market] = {};
      updatedRankScores[llm][category][market][persona] = res;

      return { ...state, rank_scores: updatedRankScores, brand_scores: existingBrandScores };
    }

    case "UPDATE_CATEGORY_DIMENSIONS": {
      const { llm, category, market, persona, data, summary, sources, baseResponses, isValidated } = action.payload;
      const updated = { ...state.category_dimensions };
      if (!updated[llm]) updated[llm] = {};
      if (!updated[llm][category]) updated[llm][category] = {};
      if (!updated[llm][category][market]) updated[llm][category][market] = {};
      updated[llm][category][market][persona] = { data, summary, sources, baseResponses, isValidated };
      return { ...state, category_dimensions: updated };
    }

    case "UPDATE_BRAND_DESCRIPTION": {
      const { res, req } = action.payload;
      const { category, llm } = req;
      const updated = { ...state.brand_description };
      if (!updated[llm]) updated[llm] = {};
      updated[llm][category] = res;
      return { ...state, brand_description: updated };
    }

    case "UPDATE_POSITIVE_NEGATIVE_ATTRIBUTES": {
      const { res, req } = action.payload;
      const { llm, category, market, persona } = req;
      const updated = { ...state.positive_negative_attributes };
      if (!updated[llm]) updated[llm] = {};
      if (!updated[llm][category]) updated[llm][category] = {};
      if (!updated[llm][category][market]) updated[llm][category][market] = {};
      const existing = updated[llm][category][market][persona] || [];
      const newEntry = { ...res };
      const existingIdx = existing.findIndex((e) => e.name === res.name);
      if (existingIdx >= 0) {
        const copy = [...existing];
        copy[existingIdx] = newEntry;
        updated[llm][category][market][persona] = copy;
      } else {
        updated[llm][category][market][persona] = [...existing, newEntry];
      }
      return { ...state, positive_negative_attributes: updated };
    }

    case "UPDATE_CATEGORY": {
      const category = Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean);
      const llm = state.llm;
      const persona = state.persona;
      const markets = state.markets;
      return {
        ...state,
        category,
        brand_scores: updateScores({ ...state.brand_scores }, llm, category, markets, persona),
        rank_scores: updateScores({ ...state.rank_scores }, llm, category, markets, persona),
        core_attributes: updateScores({ ...state.core_attributes }, llm, category, markets, persona),
        category_dimensions: updateScores({ ...state.category_dimensions }, llm, category, markets, persona),
        sources: updateScores({ ...state.sources }, llm, category, markets),
        brand_description: updateScores({ ...state.brand_description }, llm, category),
        positive_negative_attributes: updateScores({ ...state.positive_negative_attributes }, llm, category, markets, persona),
      };
    }

    case "UPDATE_LLM": {
      const llm = Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean);
      const category = state.category;
      const persona = state.persona;
      const markets = state.markets;
      return {
        ...state,
        llm,
        brand_scores: updateScores({ ...state.brand_scores }, llm, category, markets, persona),
        rank_scores: updateScores({ ...state.rank_scores }, llm, category, markets, persona),
        core_attributes: updateScores({ ...state.core_attributes }, llm, category, markets, persona),
        category_dimensions: updateScores({ ...state.category_dimensions }, llm, category, markets, persona),
        sources: updateScores({ ...state.sources }, llm, category, markets),
        brand_description: updateScores({ ...state.brand_description }, llm, category),
        positive_negative_attributes: updateScores({ ...state.positive_negative_attributes }, llm, category, markets, persona),
      };
    }

    case "UPDATE_MARKET": {
      const markets = Array.isArray(action.payload)
        ? action.payload.map((m) => m.trim())
        : [action.payload].filter(Boolean).map((m) => m.trim());
      const llm = state.llm;
      const category = state.category;
      const persona = state.persona;
      return {
        ...state,
        market: markets[0] || "",
        markets,
        brand_scores: updateScores({ ...state.brand_scores }, llm, category, markets, persona),
        rank_scores: updateScores({ ...state.rank_scores }, llm, category, markets, persona),
        core_attributes: updateScores({ ...state.core_attributes }, llm, category, markets, persona),
        category_dimensions: updateScores({ ...state.category_dimensions }, llm, category, markets, persona),
        sources: updateScores({ ...state.sources }, llm, category, markets),
        brand_description: updateScores({ ...state.brand_description }, llm, category),
        positive_negative_attributes: updateScores({ ...state.positive_negative_attributes }, llm, category, markets, persona),
      };
    }

    case "UPDATE_PERSONA": {
      const persona = action.payload;
      const llm = state.llm;
      const category = state.category;
      const markets = state.markets;
      return {
        ...state,
        persona,
        brand_scores: updateScores({ ...state.brand_scores }, llm, category, markets, persona),
        rank_scores: updateScores({ ...state.rank_scores }, llm, category, markets, persona),
      };
    }

    case "UPDATE_COMPOSITE_RANK_SCORES": {
      const { req, res } = action.payload;
      const { category, market, persona } = req;
      const updated = { ...state.composite_rank_score };
      if (!updated[category]) updated[category] = {};
      if (!updated[category][market]) updated[category][market] = {};
      updated[category][market][persona] = res;
      return { ...state, composite_rank_score: updated };
    }

    case "REFRESH_STATE": {
      const freshScores = updateScores(
        {},
        state.llm,
        state.category,
        state.markets,
        state.persona
      );
      const freshRankScores = updateScores(
        {},
        state.llm,
        state.category,
        state.markets,
        state.persona
      );
      return {
        ...state,
        brand_scores: freshScores,
        rank_scores: freshRankScores,
        composite_rank_score: {},
        core_attributes: {},
        sources: {},
        category_dimensions: {},
        brand_description: {},
        positive_negative_attributes: {},
        status: {},
        showAnimation: true,
      };
    }

    case "RESET_STATE":
      return {
        ...CA_INITIAL_STATE,
        transactionHistory: state.transactionHistory,
      };

    case "RESET_FOR_RELAUNCH":
      return {
        ...CA_INITIAL_STATE,
        category: state.category,
        market: state.market,
        markets: state.markets,
        persona: state.persona,
        llm: state.llm,
        primary_llm: state.primary_llm,
        type: state.type,
        competitors: state.competitors,
        transactionHistory: state.transactionHistory,
        auditLimits: state.auditLimits,
        state: {
          ...CA_INITIAL_NESTED_STATE,
          keywords: state.state?.keywords,
          selectedMetrics: state.state?.selectedMetrics,
          rbiType: state.state?.rbiType,
          conversationSettings: state.state?.conversationSettings,
          personaSettings: state.state?.personaSettings,
          customTemplateID: state.state?.customTemplateID,
        },
      };

    case "UPDATE_TRANSACTION_HISTORY": {
      const payload = action.payload;
      if (payload?.clear) return { ...state, transactionHistory: [] };
      if (payload?.replace) return { ...state, transactionHistory: payload.data };
      return { ...state, transactionHistory: Array.isArray(payload) ? payload : [payload] };
    }

    case "UPDATE_LAUNCH_RECORD": {
      const payload = action.payload;
      const existingRecord = state.launchRecord || [];

      // If payload has no name, patch the last record (e.g. assigning transactionId after launch API response)
      if (!payload?.name) {
        if (existingRecord.length === 0) {
          return { ...state, launchRecord: [payload] };
        }
        const updated = [...existingRecord];
        updated[updated.length - 1] = { ...updated[updated.length - 1], ...payload };
        return { ...state, launchRecord: updated };
      }

      const isFinalAnalysis = payload?.name === "Final Analysis";
      const hasFinal = existingRecord.some((r) => r.name === "Final Analysis");
      if (isFinalAnalysis && hasFinal) {
        return {
          ...state,
          launchRecord: existingRecord.map((r) =>
            r.name === "Final Analysis" ? { ...r, ...payload } : r
          ),
        };
      }
      return { ...state, launchRecord: [...existingRecord, payload] };
    }

    case "UPDATE_STATUS": {
      let { totalCount, completedTaskCount } = action.payload;
      totalCount = totalCount ?? state.state?.status?.totalCount ?? 0;
      completedTaskCount = completedTaskCount ?? state.state?.status?.completedTaskCount ?? 0;
      return {
        ...state,
        state: {
          ...state.state,
          status: { totalCount, completedTaskCount },
        },
      };
    }

    default: {
      if (action.type === "UPDATE_SELECTED_MARKET") {
        const { persona, market, category, isCombinationAllowed } = resolveSelectionRules(state, null, action.payload, null);
        return { ...state, isCombinationAllowed, state: { ...state.state, selectedPersona: persona, selectedMarket: market, selectedCategory: category } };
      }
      if (action.type === "UPDATE_SELECTED_PERSONA") {
        const { persona, market, category, isCombinationAllowed } = resolveSelectionRules(state, action.payload, null, null);
        return { ...state, isCombinationAllowed, state: { ...state.state, selectedPersona: persona, selectedMarket: market, selectedCategory: category } };
      }
      if (action.type === "UPDATE_SELECTED_CATEGORY") {
        const { persona, market, category, isCombinationAllowed } = resolveSelectionRules(state, null, null, action.payload);
        return { ...state, isCombinationAllowed, state: { ...state.state, selectedPersona: persona, selectedMarket: market, selectedCategory: category } };
      }

      const simple = {
        UPDATE_LOADING: "loading",
        UPDATE_ID: "id",
        UPDATE_ENTITY: "entity",
        UPDATE_NAME: "name",
        UPDATE_TYPE: "type",
        UPDATE_DOMAIN: "domain",
        UPDATE_TRANSACTION_ID: "transactionId",
        UPDATE_FINAL_TRANSACTION_ID: "finalTransactionId",
        UPDATE_CUSTOM_TEMPLATE_ID: "customTemplateID",
        UPDATE_TRANSACTION_STATUS: "transactionStatus",
        UPDATE_FINAL_TRANSACTION_STATUS: "finalTransactionStatus",
        UPDATE_SOURCE_VALIDATION_STATUS: "sourceValidationStatus",
        UPDATE_LAUNCHED: "is_launched",
        UPDATE_ABORT_LAUNCH: "abort_launch",
        UPDATE_FORM_DATA: "formData",
        UPDATE_LAUNCH_TRANSACTION: "is_launchTransaction",
        RESET_FORM_STATE: "is_resetFormState",
        UPDATE_SELECTED_LLM: "selectedLLM",
        UPDATE_SELECTED_COMPETITOR: "selectedCompetitor",
        UPDATE_STATUS: "status",
        UPDATE_RBI_TYPE: "rbiType",
        UPDATE_KEYWORDS: "keywords",
        UPDATE_IS_BATCH_MODE: "isBatchMode",
        UPDATE_RANK_COMPETITIVE_SET: "rankCompetitiveSet",
        UPDATE_RANK_METHODOLOGY: "rankMethodology",
        UPDATE_SELECTED_METRICS: "selectedMetrics",
        UPDATE_PRIMARY_LLM: "primary_llm",
        UPDATE_PERSONA_SETTINGS: "personaSettings",
        UPDATE_CONVERSATION_SETTINGS: "conversationSettings",
        UPDATE_CONVERSATION_ENABLED: "conversationEnabled",
        UPDATE_AUDIT_LIMITS: "auditLimits",
        SHOW_ANIMATION: "showAnimation",
        UPDATE_COMPARE_MODE: "compareMode",
        UPDATE_MULTI_LLMS: "multiLLMs",
        UPDATE_IS_SHARED: "isShared",
        UPDATE_SHARED_AT: "sharedAt",
        UPDATE_OWNER_USER_ID: "ownerUserId",
      };
      const key = simple[action.type];
      if (key) {
        // Handle nested state fields (mirrors BA's discovery-context structure)
        if (
          [
            "id", "transactionId", "finalTransactionId", "customTemplateID",
            "transactionStatus", "finalTransactionStatus", "sourceValidationStatus",
            "selectedLLM", "selectedCompetitor", "status", "rbiType", "keywords", "isBatchMode", "rankCompetitiveSet", "rankMethodology", "selectedMetrics", "showReportCheckbox",
            "selectAll", "selectedSection", "personaSettings", "conversationSettings",
            "conversationEnabled", "metricSettingObject", "showAnimation",
            "compareMode", "multiLLMs", "isShared", "sharedAt", "ownerUserId",
          ].includes(key)
        ) {
          return { ...state, state: { ...state.state, [key]: action.payload } };
        }
        return { ...state, [key]: action.payload };
      }
      if (action.type === "UPDATE_COMPETITORS") {
        return { ...state, competitors: action.payload };
      }
      if (action.type === "SHOW_REPORT_CHECKBOX") {
        return { ...state, state: { ...state.state, showReportCheckbox: action.payload } };
      }
      if (action.type === "CLICK_COMPONENT_CHECKBOX") {
        const { uniqueKey, filter, checked, id } = action.payload;
        const existing = { ...state.state.selectedSection };
        if (checked) {
          delete existing[uniqueKey];
        } else {
          existing[uniqueKey] = filter;
        }
        return { ...state, state: { ...state.state, selectedSection: existing, showReportCheckbox: true } };
      }
      if (action.type === "SET_METRIC_SETTING_OBJ") {
        return { ...state, state: { ...state.state, metricSettingObject: action.payload } };
      }
      return state;
    }
  }
}

export default function CategoryAnalysisProvider({ children }) {
  const { customerUiInfo } = useContext(AuthContext);
  const [page, setPage] = useState(1);
  const [state, dispatch] = useReducer(caReducer, {
    ...CA_INITIAL_STATE,
    keywords: resolveDefaultKeywordOptions(customerUiInfo),
    state: { ...CA_INITIAL_NESTED_STATE, keywords: resolveDefaultKeywordOptions(customerUiInfo) },
  });

  // Flatten nested state for convenience
  const flatState = {
    ...state,
    ...(state.state || {}),
  };

  const ctx = useMemo(() => ({
    // Data state
    entity: state.entity,
    name: state.name,
    type: state.type || "brand",
    domain: state.domain,
    category: state.category || [],
    market: state.markets?.[0] || "",
    markets: state.markets || [],
    llm: state.llm || ["gpt-5.6"],
    primary_llm: state.primary_llm || "gpt-5.6",
    persona: state.persona || [],
    loading: state.loading,
    formData: state.formData,
    is_resetFormState: state.is_resetFormState,
    is_launched: state.is_launched,
    abort_launch: state.abort_launch,
    competitors: state.competitors || {},
    brand_scores: state.brand_scores || {},
    rank_scores: state.rank_scores || {},
    composite_rank_score: state.composite_rank_score || {},
    core_attributes: state.core_attributes || {},
    sources: state.sources || {},
    category_dimensions: state.category_dimensions || {},
    brand_description: state.brand_description || {},
    positive_negative_attributes: state.positive_negative_attributes || {},
    customTemplateID: state.state?.customTemplateID,
    finalTransactionId: state.state?.finalTransactionId,
    conversationEnabled: state.state?.conversationEnabled || false,
    conversationSettings: state.state?.conversationSettings || { selectedSites: [], excludeBrandOwnedSources: false },
    personaSettings: state.state?.personaSettings || { applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false },
    isCombinationAllowed: state.isCombinationAllowed !== false,
    auditLimits: state.auditLimits,
    compareMode: state.state?.compareMode || "default",
    multiLLMs: state.state?.multiLLMs || [],

    // Nested state object (matches BA's context.state shape for sub-components)
    state: {
      id: state.state?.id,
      transactionId: state.state?.transactionId,
      finalTransactionId: state.state?.finalTransactionId,
      customTemplateID: state.state?.customTemplateID,
      transactionStatus: state.state?.transactionStatus || "",
      finalTransactionStatus: state.state?.finalTransactionStatus || "INITIATED",
      sourceValidationStatus: state.state?.sourceValidationStatus || "PENDING",
      selectedLLM: state.state?.selectedLLM || "gpt-5.6",
      selectedCompetitor: state.state?.selectedCompetitor || 0,
      selectedCategory: state.state?.selectedCategory || "",
      selectedMarket: state.state?.selectedMarket || "",
      selectedPersona: state.state?.selectedPersona || "General Persona",
      status: state.state?.status || {},
      rbiType: state.state?.rbiType || "propensityToRecommend",
      keywords: state.state?.keywords || resolveDefaultKeywordOptions(customerUiInfo),
      selectedMetrics: state.state?.selectedMetrics || ALL_METRIC_VALUES,
      showReportCheckbox: state.state?.showReportCheckbox || false,
      selectAll: state.state?.selectAll || false,
      selectedSection: state.state?.selectedSection || {},
      personaSettings: state.state?.personaSettings || { applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false },
      conversationSettings: state.state?.conversationSettings || { selectedSites: [], excludeBrandOwnedSources: false },
      conversationEnabled: state.state?.conversationEnabled || false,
      metricSettingObject: state.state?.metricSettingObject || {},
      showAnimation: state.state?.showAnimation !== false,
      launchRecord: state.launchRecord || [],
      transactionHistory: state.transactionHistory || [],
      rank_scores: state.rank_scores || {},
      brand_scores: state.brand_scores || {},
      compareMode: state.state?.compareMode || "default",
      multiLLMs: state.state?.multiLLMs || [],
      isBatchMode: state.state?.isBatchMode || false,
      rankCompetitiveSet: state.state?.rankCompetitiveSet || [],
      rankMethodology: state.state?.rankMethodology || "by_category",
    },

    // Pagination
    page,
    setPage,

    // Update functions
    updateId: (id) => dispatch({ type: "UPDATE_ID", payload: id }),
    updateLoading: (val) => dispatch({ type: "UPDATE_LOADING", payload: val }),
    updateEntity: (entity) => dispatch({ type: "UPDATE_ENTITY", payload: entity }),
    updateName: (name) => dispatch({ type: "UPDATE_NAME", payload: name }),
    updateType: (type) => dispatch({ type: "UPDATE_TYPE", payload: type }),
    updateDomain: (domain) => dispatch({ type: "UPDATE_DOMAIN", payload: domain }),
    updateCategory: (category) => dispatch({ type: "UPDATE_CATEGORY", payload: category }),
    updateMarket: (markets) => dispatch({ type: "UPDATE_MARKET", payload: markets }),
    updateLLM: (llm) => dispatch({ type: "UPDATE_LLM", payload: llm }),
    updatePersona: (persona) => dispatch({ type: "UPDATE_PERSONA", payload: persona }),
    updatePrimaryLLM: (llm) => dispatch({ type: "UPDATE_PRIMARY_LLM", payload: llm }),
    updatedCompetitors: (competitors) => dispatch({ type: "UPDATE_COMPETITORS", payload: competitors }),
    updateBrandScores: (payload) => dispatch({ type: "UPDATE_BRAND_SCORE", payload }),
    replaceBrandScores: (scores) => {
      dispatch({ type: "REPLACE_BRAND_SCORES", payload: scores });
    },
    updateSources: (payload) => dispatch({ type: "UPDATE_SOURCES", payload }),
    updateCoreAttributes: (payload) => dispatch({ type: "UPDATE_CORE_ATTRIBUTES", payload }),
    updateRankScores: (payload) => dispatch({ type: "UPDATE_RANK_SCORES", payload }),
    updateCompositeRankScores: (payload) => dispatch({ type: "UPDATE_COMPOSITE_RANK_SCORES", payload }),
    updateCategoryDimensions: (payload) => dispatch({ type: "UPDATE_CATEGORY_DIMENSIONS", payload }),
    updateBrandDescription: (payload) => dispatch({ type: "UPDATE_BRAND_DESCRIPTION", payload }),
    updatePositiveNegativeAttributes: (payload) => dispatch({ type: "UPDATE_POSITIVE_NEGATIVE_ATTRIBUTES", payload }),
    updateTransactionId: (id) => dispatch({ type: "UPDATE_TRANSACTION_ID", payload: id }),
    updateFinalTransactionId: (id) => dispatch({ type: "UPDATE_FINAL_TRANSACTION_ID", payload: id }),
    updateCustomTemplateID: (id) => dispatch({ type: "UPDATE_CUSTOM_TEMPLATE_ID", payload: id }),
    updateTransactionStatus: (status) => dispatch({ type: "UPDATE_TRANSACTION_STATUS", payload: status }),
    updateFinalTransactionStatus: (status) => dispatch({ type: "UPDATE_FINAL_TRANSACTION_STATUS", payload: status }),
    updateSourceValidationStatus: (status) => dispatch({ type: "UPDATE_SOURCE_VALIDATION_STATUS", payload: status }),
    updateTransactionHistory: (data) => dispatch({ type: "UPDATE_TRANSACTION_HISTORY", payload: data }),
    updateSelectedLLM: (llm) => dispatch({ type: "UPDATE_SELECTED_LLM", payload: llm }),
    updateSelectedCompetitor: (idx) => dispatch({ type: "UPDATE_SELECTED_COMPETITOR", payload: idx }),
    updateSelectedCategory: (cat) => dispatch({ type: "UPDATE_SELECTED_CATEGORY", payload: cat }),
    updateSelectedMarket: (market) => dispatch({ type: "UPDATE_SELECTED_MARKET", payload: market }),
    updateSelectedPersona: (persona) => dispatch({ type: "UPDATE_SELECTED_PERSONA", payload: persona }),
    updateStatus: (status) => dispatch({ type: "UPDATE_STATUS", payload: status }),
    updateLaunchRecord: (record) => dispatch({ type: "UPDATE_LAUNCH_RECORD", payload: record }),
    updateFormData: (val) => dispatch({ type: "UPDATE_FORM_DATA", payload: val }),
    updateLaunchTransaction: (val) => dispatch({ type: "UPDATE_LAUNCH_TRANSACTION", payload: val }),
    updateSourceValidationStatus: (status) => dispatch({ type: "UPDATE_SOURCE_VALIDATION_STATUS", payload: status }),
    updatePersonaSettings: (settings) => dispatch({ type: "UPDATE_PERSONA_SETTINGS", payload: settings }),
    updateRbiType: (type) => dispatch({ type: "UPDATE_RBI_TYPE", payload: type }),
    updateKeywords: (keywords) => dispatch({ type: "UPDATE_KEYWORDS", payload: keywords }),
    updateIsBatchMode: (isBatchMode) => dispatch({ type: "UPDATE_IS_BATCH_MODE", payload: isBatchMode }),
    updateRankCompetitiveSet: (val) => dispatch({ type: "UPDATE_RANK_COMPETITIVE_SET", payload: val }),
    updateRankMethodology: (val) => dispatch({ type: "UPDATE_RANK_METHODOLOGY", payload: val }),
    updateSelectedMetrics: (selectedMetrics) => dispatch({ type: "UPDATE_SELECTED_METRICS", payload: selectedMetrics }),
    updateConversationSetting: (settings) => dispatch({ type: "UPDATE_CONVERSATION_SETTINGS", payload: settings }),
    updateConversationEnabled: (val) => dispatch({ type: "UPDATE_CONVERSATION_ENABLED", payload: val }),
    updateLaunch: (limits) => dispatch({ type: "UPDATE_AUDIT_LIMITS", payload: limits }),
    updatedLaunched: (val) => dispatch({ type: "UPDATE_LAUNCHED", payload: val }),
    updateAbortLaunch: (val) => dispatch({ type: "UPDATE_ABORT_LAUNCH", payload: val }),
    resetFormState: (val) => dispatch({ type: "RESET_FORM_STATE", payload: val }),
    showReportCheckbox: (val) => dispatch({ type: "SHOW_REPORT_CHECKBOX", payload: val }),
    clickComponentCheckbox: (payload) => dispatch({ type: "CLICK_COMPONENT_CHECKBOX", payload }),
    setMetricSettingObj: (obj) => dispatch({ type: "SET_METRIC_SETTING_OBJ", payload: obj }),
    cancelSelection: () => {},
    showAnimation: () => dispatch({ type: "SHOW_ANIMATION", payload: true }),
    updateCompareMode: (mode) => dispatch({ type: "UPDATE_COMPARE_MODE", payload: mode }),
    updateMultiLLMs: (llms) => dispatch({ type: "UPDATE_MULTI_LLMS", payload: llms }),
    updateShareInfo: (data) => {
      dispatch({ type: "UPDATE_IS_SHARED", payload: !!data?.is_shared });
      dispatch({ type: "UPDATE_SHARED_AT", payload: data?.shared_at ?? null });
      dispatch({ type: "UPDATE_OWNER_USER_ID", payload: data?.owner_user_id ?? null });
    },

    mapMetricTitle: (title) => {
      const obj = state.state?.metricSettingObject;
      return obj?.metricTitleMap?.[title] ?? title ?? null;
    },

    refreshState: () => dispatch({ type: "REFRESH_STATE" }),
    resetState: () => dispatch({ type: "RESET_STATE" }),
    resetForRelaunch: () => dispatch({ type: "RESET_FOR_RELAUNCH" }),

    // Not used in CA but required for interface compatibility
    updateLanguage: () => {},
    brand_snippets: {},
    brand_themes: [],
    top_products: [],
    taxonomies: {},
    category_taxonomies: [],
    conversations: {},
    contentMapping: {},
    updateBrandSnippets: () => {},
    updatedBrandThemes: () => {},
    updatedTopProducts: () => {},
    updatedTaxonomies: () => {},
    updateCategoryTaxonomies: () => {},
    updateConversations: () => {},
    deleteConversations: () => {},
    updateContentMapping: () => {},
    updateProductPrefs: () => {},
    updatePreCategoryDimensions: () => {},
    updateEntireState: () => {},
    updateOccupation: () => {},
    updateAttribute: () => {},
    updateLocation: () => {},
    updateEntireState: () => {},
    pre_category_dimensions: [],
    product_prefs: [],
  }), [state, page]);

  return (
    <CategoryAnalysisContext.Provider value={ctx}>
      {children}
    </CategoryAnalysisContext.Provider>
  );
}

export function useIsSelectedSection({ id, uniqueKey }) {
  const ctx = useContext(CategoryAnalysisContext);
  const selectedSection = ctx?.state?.selectedSection || {};
  const checked = Object.keys(selectedSection).includes(uniqueKey);
  return { checked };
}
